import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Tab,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import InputText from "components/_refactor/text/input";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import useStructureQueries from "hooks/useStructureQueries";

import FilterCard from "components/_refactor/filter-card";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import moment from "moment";
import PeriodField from "components/_refactor/fields/period-field";
import StructureFields from "components/_refactor/fields/structure";
import TitulosFinanceiros from "./view/titulos-financeiros";
import Faturamento from "./view/faturamento";
import SituacaoFinanceira from "./view/situacao-financeira";
import Pedidos from "./view/pedidos";
import NotasFiscais from "./view/notas-fiscais";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import MaskAndValid from "utils/maskAndValid";

const dataHoje = new Date().toISOString().slice(0, 10);
const data12MesesAtras = moment(new Date())
  .subtract(12, "months")
  .format("YYYY-MM-DD");

const periodoOptions = [
  { value: 15, label: "Últimos 15 dias" },
  { value: 30, label: "Últimos 30 dias" },
  { value: 90, label: "Últimos 3 meses" },
  { value: 180, label: "Últimos 6 meses" },
  { value: 365, label: "Últimos 12 meses" },
  { value: "Outros", label: "Outros" },
];

export default function Lojista360() {
  const { getProductLines } = useStructureQueries();
  const [searching, setSearching] = useState(false);
  const [structureFilters, setStructureFilters] = useState();
  const [structureLoading, setStructureLoading] = useState([]);
  const [linhaProduto, setLinhaProduto] = useState();
  const [periodo, setPeriodo] = useState(periodoOptions[4]);
  const [dataInicial, setDataInicial] = useState(data12MesesAtras);
  const [dataFinal, setDataFinal] = useState(dataHoje);
  const [clearFilters, setClearFilters] = useState(false);
  const [canOpenTabs, setCanOpenTabs] = useState(false);
  const [cnpj, setCnpj] = useState("");
  const [faturamentoData, setFaturamentoData] = useState(null);
  const [loadingFaturamento, setLoadingFaturamento] = useState(false);

  const [situacaoFinanceiraData, setSituacaoFinanceiraData] = useState(null);
  const [loadingSituacaoFinanceira, setLoadingSituacaoFinanceira] =
    useState(false);

  const [tab, setTab] = useState(1);
  const snackbar = useSnackbar();

  const [productsLineData, setProductsLineData] = useState([]);
  const [loadingProductsLine, setLoadingProductsLine] = useState(false);

  const onCnpjChange = (e) => {
    setCnpj(MaskAndValid.maskCNPJ(e.target.value));
  };

  const fetchProductsLineData = async () => {
    setLoadingProductsLine(true);
    try {
      const result = await getProductLines();
      const res = result?.data ?? {};
      setProductsLineData(Array.isArray(res) ? res : (res.Linha ?? []));
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoadingProductsLine(false);
    }
  };

  const hasMinSearchCriteria = () =>
    !!(
      structureFilters?.idGrupoEconomico ||
      (structureFilters?.idLoja?.length > 0) ||
      structureFilters?.idMarca ||
      linhaProduto ||
      (cnpj && cnpj.replace(/\D/g, "").length === 14)
    );

  const fetchFaturamentoData = async () => {
    if (!hasMinSearchCriteria()) {
      snackbar.showMessage(
        "Preencha ao menos um filtro: Grupo econômico, Lojas, Marcas ou Linha; ou apenas CNPJ, Marca ou Linha",
        { severity: "error" }
      );
      return;
    }

    setLoadingFaturamento(true);
    setFaturamentoData(null);

    try {
      const params = new URLSearchParams({
        idMarca: structureFilters?.idMarca || "TODAS",
        idGrupoEconomico: structureFilters?.idGrupoEconomico,
        idLoja: structureFilters?.idLoja?.length ? structureFilters.idLoja : "",
        idLinhaProduto: linhaProduto?.IdProdutoLinha || "",
        dtIni: dataInicial,
        dtFim: dataFinal,
        cnpj: cnpj.replace(/\D/g, "") || "",
      });

      [...params.entries()]
        .filter(
          ([, value]) => value === undefined || value === null || value === ""
        )
        .forEach(([key]) => params.delete(key));

      const response = await Api.get(
        "visao360-faturamento",
        "reportApp",
        `?${params.toString()}`,
        true
      );

      const responseData = response?.Result || response;
      setFaturamentoData(responseData);
    } catch (err) {
      snackbar.showMessage(
        err.message || "Erro ao carregar dados de faturamento",
        {
          severity: "error",
        }
      );
    } finally {
      setLoadingFaturamento(false);
    }
  };

  const fetchSituacaoFinanceiraData = async () => {
    if (!hasMinSearchCriteria()) {
      snackbar.showMessage(
        "Preencha ao menos um filtro: Grupo econômico, Lojas, Marcas ou Linha; ou apenas CNPJ, Marca ou Linha",
        { severity: "error" }
      );
      return;
    }

    setLoadingSituacaoFinanceira(true);
    setSituacaoFinanceiraData(null);

    try {
      const params = new URLSearchParams({
        idMarca: structureFilters?.idMarca || "TODAS",
        idGrupoEconomico: structureFilters?.idGrupoEconomico,
        idLoja: structureFilters?.idLoja?.length ? structureFilters.idLoja : "",
        dtIni: dataInicial,
        dtFim: dataFinal,
        cnpj: cnpj.replace(/\D/g, "") || "",
      });

      [...params.entries()]
        .filter(
          ([, value]) => value === undefined || value === null || value === ""
        )
        .forEach(([key]) => params.delete(key));

      const response = await Api.get(
        "visao360-situacao-financeira",
        "reportApp",
        `?${params.toString()}`,
        true
      );

      const responseData = response?.Result || response;
      setSituacaoFinanceiraData(responseData);
    } catch (err) {
      snackbar.showMessage(
        err.message || "Erro ao carregar dados de situação financeira",
        {
          severity: "error",
        }
      );
    } finally {
      setLoadingSituacaoFinanceira(false);
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    setCanOpenTabs(true);

    try {
      await Promise.all([
        fetchFaturamentoData(),
        fetchSituacaoFinanceiraData(),
      ]);
    } catch (error) {
      snackbar.showMessage(error.message || "Erro ao buscar dados", {
        severity: "error",
      });
      setFaturamentoData(null);
      setSituacaoFinanceiraData(null);
      setLoadingFaturamento(false);
      setLoadingSituacaoFinanceira(false);
    } finally {
      setSearching(false);
    }
  };

  const handleClearFilters = () => {
    setCanOpenTabs(false);
    setClearFilters(true);
    setSearching(false);
    setStructureLoading([]);
    setStructureFilters(null);
    setFaturamentoData(null);
    setSituacaoFinanceiraData(null);
    setTimeout(() => {
      setClearFilters(false);
    }, 400);
  };

  useEffect(() => {
    setCanOpenTabs(false);
    setFaturamentoData([]);
    setSituacaoFinanceiraData([]);
  }, [structureFilters]);

  useEffect(() => {
    fetchProductsLineData();
  }, []);

  return (
    <>
      <HeaderClientes section="Lojista 360°" subsection="Clientes" />
      <Container maxWidth={false} style={{ marginTop: "-4rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          searching={searching}
          handleClearFilters={handleClearFilters}
          disabled={
            !hasMinSearchCriteria() ||
            searching ||
            structureLoading.length > 0
          }
        >
          <StructureFields
            onStructureFieldsChange={setStructureFilters}
            clearFilters={clearFilters}
            disableAll={searching}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={[]}
            hideFields={["channel", "representative"]}
            fieldsOrder={["economicGroup", "store"]}
            handleSearch={() => {}}
          />
          <Grid item xs={12} md={3}>
            <AutoCompleteField
              label="Linha de Produto"
              options={productsLineData}
              loading={clearFilters}
              onChange={setLinhaProduto}
              value={linhaProduto}
              disabled={searching || loadingProductsLine}
              optionLabelKey="Descricao"
              optionIdKey="IdProdutoLinha"
            />
          </Grid>
          <Grid item xs={12} md={periodo?.value === "Outros" ? 8 : 3}>
            <PeriodField
              label="Período"
              value={periodo}
              onChange={setPeriodo}
              initialDate={dataInicial}
              finalDate={dataFinal}
              setInitialDate={setDataInicial}
              setFinalDate={setDataFinal}
              disabled={searching}
              options={periodoOptions}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <InputText
              label="CNPJ"
              placeholder="00.000.000/0000-00"
              value={cnpj}
              onChange={onCnpjChange}
              disabled={searching}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid>
        </FilterCard>

        <Card hidden={!canOpenTabs}>
          <CardContent>
            <TabContext value={tab}>
              <Box>
                <TabList
                  variant="scrollable"
                  scrollButtons="false"
                  onChange={(_, newValue) => {
                    setTab(newValue);
                  }}
                >
                  <Tab label="Faturamento" value={1} />
                  <Tab label="Situação Financeira" value={2} />
                  <Tab label="Títulos Financeiros" value={3} />
                  <Tab label="Pedidos" value={4} />
                  <Tab label="Notas Fiscais" value={5} />
                </TabList>
              </Box>

              <Divider />

              <TabPanel value={1}>
                <Faturamento
                  data={faturamentoData}
                  loading={loadingFaturamento}
                />
              </TabPanel>
              <TabPanel value={2}>
                <SituacaoFinanceira
                  data={situacaoFinanceiraData}
                  loading={loadingSituacaoFinanceira}
                />
              </TabPanel>
              <TabPanel value={3}>
                <TitulosFinanceiros
                  structureFilters={{ ...structureFilters, cnpj }}
                  dataInicial={dataInicial}
                  dataFinal={dataFinal}
                />
              </TabPanel>
              <TabPanel value={4}>
                <Pedidos
                  structureFilters={{ ...structureFilters, cnpj }}
                  dataInicial={dataInicial}
                  dataFinal={dataFinal}
                />
              </TabPanel>
              <TabPanel value={5}>
                <NotasFiscais
                  structureFilters={{ ...structureFilters, cnpj }}
                  dataInicial={dataInicial}
                  dataFinal={dataFinal}
                />
              </TabPanel>
            </TabContext>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
